import { useState } from 'react';
import { PasswordField } from 'seagull';
import { useForm, Controller } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { routes } from "../../../constants";
import { handlePromise } from "../../../infra/utils";
import { useStoreActions } from "../../../contexts";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { NotificationActionType } from "../../NotificationAction";
import UnauthorizedPageContainer from "../components/UnauthorizedPageContainer";
import { FormTitle, FormFields, ErrorMessage, FormContainer, SubmitButtonField, VerticalFormFields } from "../components/FormComponents";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function SetPasswordScreen() {
  const history = useHistory();
  const location = useLocation();
  const locationState = location.state;
  const {
    logBI,
    api
  } = useStoreActions();
  const [passwordSetInProgress, setPasswordSetInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const {
    handleSubmit,
    control,
    formState,
    getValues,
    watch
  } = useForm({
    mode: 'onChange'
  });
  useSetDocumentTitle('Anchor Reset Password');
  const onSubmit = handleSubmit(async (data, e) => {
    logBI(10601);
    e === null || e === void 0 || e.preventDefault();
    setPasswordSetInProgress(true);
    const {
      password
    } = data;
    const request = api.auth.setPassword;
    const result = await handlePromise(request(password));
    if (!result.success) {
      setPasswordSetInProgress(false);
      setErrorMessage('An error occurred');
      const subscription = watch(() => {
        setErrorMessage('');
        subscription.unsubscribe();
      });
    } else {
      setPasswordSetInProgress(false);
      if ((locationState === null || locationState === void 0 ? void 0 : locationState.type) === NotificationActionType.ApproveTeamMemberInvitation) {
        history.push(routes.notificationAction, locationState);
      } else {
        history.push(routes.home);
      }
    }
  });
  return /*#__PURE__*/_jsx(UnauthorizedPageContainer, {
    children: /*#__PURE__*/_jsxs(FormContainer, {
      onSubmit: onSubmit,
      "data-testid": "set-password-form",
      children: [/*#__PURE__*/_jsx(FormTitle, {
        children: "Create new password"
      }), /*#__PURE__*/_jsx(FormFields, {
        children: /*#__PURE__*/_jsxs(VerticalFormFields, {
          children: [/*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "password",
            defaultValue: "",
            rules: {
              required: true,
              minLength: {
                value: 10,
                message: 'Use 10 or more characters'
              },
              deps: 'confirm-password'
            },
            render: _ref => {
              var _fieldState$error;
              let {
                field: {
                  onChange,
                  onBlur: _onBlur,
                  value
                },
                fieldState
              } = _ref;
              return /*#__PURE__*/_jsx(PasswordField, {
                value: value,
                onChange: onChange,
                onBlur: () => {
                  logBI(10203, {
                    isPasswordValid: !fieldState.error
                  });
                  _onBlur();
                },
                error: !!fieldState.error,
                helperText: (_fieldState$error = fieldState.error) === null || _fieldState$error === void 0 ? void 0 : _fieldState$error.message,
                label: "Password",
                testid: "password-input"
              });
            }
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "confirm-password",
            defaultValue: "",
            rules: {
              required: true,
              validate: value => {
                const doPasswordsMatch = getValues('password') === value;
                return doPasswordsMatch || 'Passwords do not match';
              }
            },
            render: _ref2 => {
              var _fieldState$error2;
              let {
                field: {
                  onChange,
                  onBlur,
                  value
                },
                fieldState,
                formState
              } = _ref2;
              return /*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(PasswordField, {
                  value: value,
                  onChange: onChange,
                  onBlur: onBlur,
                  error: !formState.isValid && !!fieldState.error,
                  helperText: !formState.isValid && ((_fieldState$error2 = fieldState.error) === null || _fieldState$error2 === void 0 ? void 0 : _fieldState$error2.message),
                  label: "Confirm password",
                  testid: "confirm-password-input"
                })
              });
            }
          })]
        })
      }), /*#__PURE__*/_jsx(SubmitButtonField, {
        disabled: !formState.isValid,
        "data-testid": "set-password-button",
        isLoading: passwordSetInProgress,
        children: "Set Password"
      }), errorMessage && /*#__PURE__*/_jsx(ErrorMessage, {
        "data-testid": "error-msg",
        children: errorMessage
      })]
    })
  });
}